import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchWallet, deriveCreditState, type CreditState, type WalletData } from './wallet';

export interface UseWalletResult {
  /** Current credit state. Starts as 'loading' until the first fetch resolves. */
  creditState: CreditState;
  /** Raw wallet data from the last successful fetch. Null until first success. */
  walletData: WalletData | null;
  /**
   * Imperatively re-fetch the wallet balance.
   *
   * On success: updates both walletData and creditState.
   * On failure:
   *   - If no prior successful fetch → transitions to 'error'
   *   - If a prior fetch succeeded → retains the last-known walletData and
   *     creditState (graceful degradation — Porter never blocks on a re-fetch failure)
   *
   * Any in-flight fetch is aborted before a new one starts.
   */
  refresh: () => void;
}

export function useWallet(): UseWalletResult {
  const [creditState, setCreditState] = useState<CreditState>('loading');
  const [walletData, setWalletData] = useState<WalletData | null>(null);

  // Stable ref to the in-flight AbortController so refresh() can cancel it.
  const abortRef = useRef<AbortController | null>(null);

  // Stable ref to the last successfully loaded WalletData.
  // Using a ref (not state) means the refresh closure always reads the current
  // value without needing it in the dependency array, avoiding stale closures.
  const walletDataRef = useRef<WalletData | null>(null);

  const refresh = useCallback(() => {
    // Cancel any in-flight fetch before starting a new one.
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    void fetchWallet(controller.signal).then((data) => {
      // Guard: don't update state if this fetch was superseded or the
      // component was unmounted.
      if (controller.signal.aborted) return;

      if (data !== null) {
        // Success: update both ref and state.
        walletDataRef.current = data;
        setWalletData(data);
        setCreditState(deriveCreditState(data));
        return;
      }
      // Failure — graceful degradation:
      // If we have never loaded successfully → show error.
      // If we have prior data → keep the last-known state.
      if (walletDataRef.current === null) {
        setCreditState('error');
      }
      // else: intentionally do nothing — retain last-known creditState
    });
  }, []);

  // Fetch on mount (panel open).
  // Cleanup aborts the in-flight fetch on unmount.
  useEffect(() => {
    refresh();
    return () => {
      abortRef.current?.abort();
    };
  }, [refresh]);

  return { creditState, walletData, refresh };
}
